import { useCallback, useMemo, useState } from 'react';
import {
  CITY_REGION_MAP,
  OnboardingPayloadSchema,
  type City,
  type Language,
  type OnboardingPayload,
  type Specialization,
  type UserRegion,
} from '@pomoshnik/shared';

/**
 * Локальный form state Stage 2 регистрации (`RegisterScreen`).
 *
 * Что хранит:
 *   - `name`, `phone` (E.164), `city`, `specialization`, `consents`
 *     (`dataProcessing` обязательное P-017 + `marketingAnalytics` опц. P-035).
 *   - `lang` НЕ внутри form state — приходит из `authStore` Container'ом и
 *     передаётся в `validate(lang)` при submit (P-008 lang меняется в шапке,
 *     не на форме).
 *
 * Что НЕ хранит:
 *   - `region` — производное от `city` через `CITY_REGION_MAP`. Возвращается
 *     как computed значение (`region`), читай НЕ через сеттер.
 *   - server-state (jwt, userId, error) — Container держит отдельно через
 *     useState + apiClient.onboarding.register (см. RegisterScreen.tsx).
 *
 * UX-нюанс при смене city:
 *   Если новый `city` принадлежит другому региону, чем предыдущий, `phone`
 *   автоматически сбрасывается в `''`. Это предотвращает «застрявший»
 *   `+71234567890` в UZ-маске после Москва → Ташкент: пользователь вводит
 *   номер заново под правильную маску. Если регион не меняется (Алматы →
 *   Астана: оба KZ) — phone сохраняется. Логика в `setField('city', ...)`.
 *
 * Validate:
 *   `validate(lang)` собирает текущее состояние в `OnboardingPayload`,
 *   парсит через `OnboardingPayloadSchema`. Возвращает `{ ok, errors,
 *   payload? }`. Сообщения ошибок — на ru (LinguiJS-localization
 *   отдельная фаза). Маппинг error → field path делается здесь, чтобы
 *   Presenter получал плоский `errors` объект и не парсил ZodError.
 */

export type FormFieldErrors = Partial<{
  name: string;
  city: string;
  specialization: string;
  phone: string;
  consents: string;
  /** Глобальная ошибка submit (server-side) — Container задаёт через `setSubmitError`. */
  form: string;
}>;

export interface OnboardingFormState {
  name: string;
  /** `''` означает «не выбрано» (Select placeholder). */
  city: City | '';
  /** `''` означает «не выбрано». */
  specialization: Specialization | '';
  /** E.164 или ''. */
  phone: string;
  consents: { dataProcessing: boolean; marketingAnalytics: boolean };
}

export interface ValidateResult {
  ok: boolean;
  errors: FormFieldErrors;
  /** Заполнен только если ok=true. */
  payload?: OnboardingPayload;
}

export interface UseOnboardingFormOptions {
  /** Стартовый город (пришёл из authStore Stage 1). Может быть null. */
  initialCity: City | null;
}

export interface UseOnboardingFormResult {
  state: OnboardingFormState;
  /** Computed: region выводится из текущего city; null если city ещё не выбран. */
  region: UserRegion | null;
  setField: <K extends 'name' | 'city' | 'specialization' | 'phone'>(
    field: K,
    value: OnboardingFormState[K],
  ) => void;
  setConsent: (key: 'dataProcessing' | 'marketingAnalytics', value: boolean) => void;
  validate: (lang: Language) => ValidateResult;
  reset: () => void;
}

/** Pure helper, экспортируется для testability mapping ZodError → FormFieldErrors. */
export function buildInitialFormState(initialCity: City | null): OnboardingFormState {
  return {
    name: '',
    city: initialCity ?? '',
    specialization: '',
    phone: '',
    consents: { dataProcessing: false, marketingAnalytics: false },
  };
}

export function useOnboardingForm(options: UseOnboardingFormOptions): UseOnboardingFormResult {
  const { initialCity } = options;
  const [state, setState] = useState<OnboardingFormState>(() => buildInitialFormState(initialCity));

  const region = useMemo<UserRegion | null>(
    () => (state.city === '' ? null : CITY_REGION_MAP[state.city]),
    [state.city],
  );

  const setField = useCallback<UseOnboardingFormResult['setField']>((field, value) => {
    setState((prev) => {
      if (field !== 'city') {
        return { ...prev, [field]: value } as OnboardingFormState;
      }
      const nextCity = value as OnboardingFormState['city'];
      const prevRegion = prev.city === '' ? null : CITY_REGION_MAP[prev.city];
      const nextRegion = nextCity === '' ? null : CITY_REGION_MAP[nextCity];
      // Регион поменялся → phone теряет актуальность под новой маской,
      // сбрасываем чтобы пользователь ввёл под правильную country code.
      const phone = prevRegion !== nextRegion ? '' : prev.phone;
      return { ...prev, city: nextCity, phone };
    });
  }, []);

  const setConsent = useCallback<UseOnboardingFormResult['setConsent']>((key, value) => {
    setState((prev) => ({ ...prev, consents: { ...prev.consents, [key]: value } }));
  }, []);

  const validate = useCallback<UseOnboardingFormResult['validate']>(
    (lang) => {
      const candidate = {
        name: state.name,
        city: state.city,
        specialization: state.specialization,
        phone: state.phone,
        lang,
        consents: state.consents,
      };
      const parsed = OnboardingPayloadSchema.safeParse(candidate);
      if (parsed.success) {
        return { ok: true, errors: {}, payload: parsed.data };
      }
      const errors: FormFieldErrors = {};
      for (const issue of parsed.error.issues) {
        const top = String(issue.path[0] ?? 'form');
        if (top === 'name' || top === 'city' || top === 'specialization' || top === 'phone') {
          if (errors[top] === undefined) {
            errors[top] = mapMessage(top, issue.message);
          }
        } else if (top === 'consents') {
          if (errors.consents === undefined) {
            errors.consents = 'Согласие на обработку персональных данных обязательно';
          }
        }
      }
      return { ok: false, errors };
    },
    [state],
  );

  const reset = useCallback(() => {
    setState(buildInitialFormState(initialCity));
  }, [initialCity]);

  return { state, region, setField, setConsent, validate, reset };
}

// ── Локализация Zod-сообщений → ru ────────────────────────────────────────
// Текстовки фиксированы для Phase 2 (LinguiJS — отдельная фаза). Ключевые
// случаи покрыты явно; для всех остальных — generic «Заполните поле».
//
// Phone намеренно не различает «пусто» vs «неверный формат» в Phase 2: оба
// кейса показывают «Неверный формат телефона». Юзер так или иначе должен
// ввести E.164-валидный номер; differentiation добавит шум без UX-выгоды.
// LinguiJS Phase введёт более тонкую формулировку при необходимости.
function mapMessage(field: string, raw: string): string {
  if (field === 'name') {
    if (raw.includes('слишком короткое')) return 'Имя слишком короткое';
    if (raw.includes('слишком длинное')) return 'Имя слишком длинное';
    return 'Имя обязательно';
  }
  if (field === 'phone') {
    return 'Неверный формат телефона';
  }
  if (field === 'city') return 'Выберите город';
  if (field === 'specialization') return 'Выберите специализацию';
  return 'Заполните поле';
}
